import { LabResourcePicker } from './LabResources';
import type { components } from '../../api/schema';
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, canOperate, type Agent, type Campaign } from "../../api/client";
import { Async, Field, Modal, Page, Table, relative, useAsync } from "../../components";

/**
 * Realms: lab and production environments recorded run by run. A realm names the shards in
 * scope and the playbooks to run against them; each start and stop of a playbook
 * is a run, with a report of everything the agents measured and reported in
 * between — exportable to Word.
 */
export default function Campaigns() {
  const campaigns = useAsync((s) => api.get<Campaign[]>("/campaigns", s));
  const agents = useAsync((s) => api.get<Agent[]>("/agents", s));
  const [creating, setCreating] = useState(false);
  const navigate = useNavigate();
  const names = Object.fromEntries((agents.data ?? []).map((a) => [a.id, a.name]));

  return (
    <Page
      title="Realms"
      subtitle="Infrastructure, access and playbooks for lab and production environments."
      actions={canOperate() ? <button className="primary" onClick={() => setCreating(true)}>New realm</button> : null}
    >
      <Async state={campaigns} empty="No realms yet. Create one for a lab or production environment: a failover test, a change window, a vendor evaluation.">
        {(rows: Campaign[]) => (
          <Table head={<><th>Realm</th><th>Shards</th><th>Created</th></>}>
            {rows.map((c) => (
              <tr key={c.id}>
                <td>
                  <Link to={`/campaigns/${c.id}`}>{c.name}</Link>
                  {c.description ? <div className="muted" style={{ fontSize: 12 }}>{c.description}</div> : null}
                </td>
                <td>{c.agent_ids.map((id) => names[id] ?? id.slice(0, 8)).join(", ") || <span className="muted">none</span>}</td>
                <td>{relative(c.created_at)}</td>
              </tr>
            ))}
          </Table>
        )}
      </Async>
      {creating ? (
        <CampaignForm
          agents={agents.data ?? []}
          onClose={() => setCreating(false)}
          onSaved={(c) => { setCreating(false); navigate(`/campaigns/${c.id}`); }}
        />
      ) : null}
    </Page>
  );
}

/** Create or edit a campaign: its name, description and agents in scope. */
export function CampaignForm({ campaign, agents, onClose, onSaved }: {
  campaign?: Campaign;
  agents: Agent[];
  onClose: () => void;
  onSaved: (c: Campaign) => void;
}) {
  const [name, setName] = useState(campaign?.name ?? "");
  const [description, setDescription] = useState(campaign?.description ?? "");
  const [chosen, setChosen] = useState<string[]>(campaign?.agent_ids ?? []);
  const [resources,setResources]=useState<components['schemas']['LabResource'][]>(campaign?.resources||[]);
  const [resourcesReady,setResourcesReady]=useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    const body = { name: name.trim(), description: description.trim(), agent_ids: chosen,resources,...(campaign?{expected_resources_revision:campaign.resources_revision||1}:{}) };
    try {
      const saved = campaign
        ? await api.put<Campaign>(`/campaigns/${campaign.id}`, body)
        : await api.post<Campaign>("/campaigns", body);
      onSaved(saved);
    } catch (err) {
      setError(`Not saved: ${err instanceof Error ? err.message : String(err)}`);
      setBusy(false);
    }
  }

  return (
    <Modal title={campaign ? "Edit realm" : "New realm"} onClose={onClose}>
      <form onSubmit={submit}>
        <Field label="Name" value={name} onChange={setName} placeholder="Branch WAN failover, October" />
        <Field label="Description (optional)" value={description} onChange={setDescription} placeholder="Environment, purpose and expected outcomes" />
        <fieldset style={{ border: 0, padding: 0, margin: "12px 0 0" }}>
          <legend style={{ fontWeight: 600, fontSize: 13, marginBottom: 6 }}>Shards in scope</legend>
          {agents.length === 0 ? <div className="muted">No shards enrolled yet.</div> : agents.map((a) => (
            <label key={a.id} style={{ display: "block", fontWeight: 400 }}>
              <input
                type="checkbox"
                checked={chosen.includes(a.id)}
                onChange={(e) => setChosen(e.target.checked ? [...chosen, a.id] : chosen.filter((id) => id !== a.id))}
              />{" "}
              {a.name}
            </label>
          ))}
          <div className="muted" style={{ fontSize: 12, marginTop: 6 }}>
            During a run, every lightweight test on these shards probes every second, and what they measure and report goes into the run&apos;s report.
            {campaign ? " A run already in progress keeps the shards it started with." : ""}
          </div>
        </fieldset>
        <LabResourcePicker chosen={resources} onChange={setResources} onReady={setResourcesReady}/>
        {error ? <div className="banner error" role="alert" style={{ marginTop: 14 }}>{error}</div> : null}
        <div className="row" style={{ marginTop: 18, justifyContent: "flex-end" }}>
          <button type="button" onClick={onClose}>Cancel</button>
          <button className="primary" type="submit" disabled={busy || !name.trim() || !resourcesReady || resources.length>100}>{busy ? "Saving…" : "Save"}</button>
        </div>
      </form>
    </Modal>
  );
}
